import { HostAppName, HostAppSettingName, HostAppSettings } from "../office-js/HostAppSettings";
import { OneNotePageError, OneNotePageErrors } from "./OneNotePageErrors";
import { ErrorStringFromException, isNullOrUndefined } from "../../utils/utils";
import dispatcher from "../../frontend/dispatcher";
import { HostAppUtil } from "../office-js/HostAppUtil";
import { HostAppEventHandlerFactory } from "../factory/HostAppEventHandlerFactory";
import { HostAppEvent } from "../manager/eventManager/HostAppEventReceiver";

export const OneNoteManagerActionTypes = {
    OneNoteAssociatedPageUpdate: 'ONE_NOTE_ASSOCIATED_PAGE_UPDATE'
};

class OneNoteTranscriptionManager {

    private static notebookNameKey: string  = 'name';
    private static sectionNameKey: string  = 'name';
    private static pageNameKey: string  = 'title';
    
    private associatedPageInfo: AssociatedPageInfo;

    constructor() {
        this.associatedPageInfo = new AssociatedPageInfo();
    }

    public Initialize(): void {
        HostAppEventHandlerFactory.GetInstance().on(HostAppEvent[HostAppEvent.ONENOTE_PAGE_CHANGED], this.onPageChange);
        this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired();
    }

    private onPageChange = () => {
        this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired();
    }

    private SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired = async () => {

        const oldPageInfo = AssociatedPageInfo.clone(this.associatedPageInfo);
        await this.LoadLatestInfoForAssociatedPage();
        if (this.IsPageInfoChanged(oldPageInfo)) {

            //Notify listeners as OneNote page info has changed.
            dispatcher.dispatch({
                type: OneNoteManagerActionTypes.OneNoteAssociatedPageUpdate,
            });
        }
    }

    private IsPageInfoChanged(oldPageInfo: AssociatedPageInfo): boolean {
        if (isNullOrUndefined(oldPageInfo) || (oldPageInfo.notebookName !== this.associatedPageInfo.notebookName ||
            oldPageInfo.sectionName !== this.associatedPageInfo.sectionName ||
            oldPageInfo.pageName !== this.associatedPageInfo.pageName)) {
            return true;
        }
        return false;
    }

    private async LoadLatestInfoForAssociatedPage() {
        await OneNote.run(async (context) => {
            try {
                const notebook = context.application.notebooks.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteNotebookId));
                const section = notebook.sections.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteSectionId));
                const page = section.pages.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId));

                //Load notebook, section and page name.
                notebook.load(OneNoteTranscriptionManager.notebookNameKey);
                section.load(OneNoteTranscriptionManager.sectionNameKey);
                page.load(OneNoteTranscriptionManager.pageNameKey);
                await context.sync();

                //Read the loaded information to pageInfo object.
                this.associatedPageInfo.notebookName = notebook.name;
                this.associatedPageInfo.sectionName = section.name;
                this.associatedPageInfo.pageName = page.title;
            } catch (e) {
                throw new OneNotePageError(OneNotePageErrors.OneNotePageNotFound, ErrorStringFromException(e));
            }
        });
    }

    public GetAssociatedNoteBookName() {
        return this.associatedPageInfo.notebookName; //associatedNoteBookName
    }

    public RefreshPageInfoRequest() {
        this.SyncAssociatedPageInfoFromOneNoteAndNotifyIfRequired();
    }

    public GetAssociatedSectionName() {
        return this.associatedPageInfo.sectionName; //associatedSectionName
    }

    public GetAssociatedPageName() {
        return this.associatedPageInfo.pageName; //associatedPageName
    }
}

class AssociatedPageInfo {

    notebookName: string;
    sectionName: string;
    pageName: string;

    constructor(notebookName: string = '', sectionName: string = '', pageName: string = '') {
        this.notebookName = notebookName;
        this.sectionName = sectionName;
        this.pageName = pageName;
    }

    static clone(associatedPageInfo : AssociatedPageInfo): AssociatedPageInfo {
        let clonedPageInfo = new AssociatedPageInfo();
        clonedPageInfo.notebookName = associatedPageInfo.notebookName;
        clonedPageInfo.sectionName = associatedPageInfo.sectionName;
        clonedPageInfo.pageName = associatedPageInfo.pageName;
        return clonedPageInfo;
    }
}

export const NavigateToAssociatedPage = async () => {
    await OneNote.run(async (context) => {
        try {
            const notebook = context.application.notebooks.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteNotebookId));
            const section = notebook.sections.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNoteSectionId));
            const page = section.pages.getItem(HostAppSettings.GetSettingAsString(HostAppSettingName.AssociatedOneNotePageId));

            // Open the page on OneNote side.
            context.application.navigateToPage(page);
        } catch (e) {
            throw new OneNotePageError(OneNotePageErrors.OneNotePageNotFound, ErrorStringFromException(e));
        }
    });
}

export const InitializeOneNoteTranscriptionManager = () => {
    oneNoteTranscriptionManager.Initialize();
}

export const ShouldShowOneNotePageInfoBar = () => {
    //only show this bar for oneNote.
    return HostAppUtil.GetHostAppName() === HostAppName.ONENOTE;
}

const oneNoteTranscriptionManager = new OneNoteTranscriptionManager();
export default oneNoteTranscriptionManager;